import React from 'react';
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../Widget/ControllerContext';
import {TPAComponentsProvider} from '@wix/wix-ui-tpa/cssVars';
import {getDirValue} from '../../../domain/utils/getDirValue';
import {pageTheme} from '../../../common/components/WithThemeOverride/pageTheme';
import {LoadingLayout} from './LoadingLayout';
import {LoadedLayoutLazy} from './LoadedLayoutLazy';
import {SkipCheckoutLayout} from '../SkipCheckoutLayout/SkipCheckoutLayout';
import {SkipCheckoutFlowStatus} from '../../../types/checkoutApp.types';
import {SPECS} from '../../../common/constants';

export enum CheckoutAppPageDataHook {
  root = 'CheckoutAppPageDataHook.root',
}

const CheckoutApp = () => {
  const {failedToFetch, isLoading, isOneColumnView, checkoutSettingsStore, navigationStore, checkoutStore} =
    useControllerProps();
  const {isRTL, isMobile} = useEnvironment();
  const {experiments} = useExperiments();

  const hasNoPremium = !isLoading && !checkoutSettingsStore?.isPremium;
  const hasOldPremiumError =
    !experiments.enabled(SPECS.HandleCheckoutErrorStates) && hasNoPremium && !navigationStore?.isOwnerOrPreview;
  const hasError = failedToFetch || hasOldPremiumError;

  const loadingLayout = <LoadingLayout isOneColumnView={isOneColumnView} />;
  const loadedLayout = (
    <LoadedLayoutLazy
      fallback={loadingLayout}
      dir={experiments.enabled(SPECS.RTLCheckoutSkeleton) ? undefined : getDirValue(isRTL)}
      showErrorState={hasError}
      hasPremiumError={hasOldPremiumError}
      loaded={!isLoading}
    />
  );

  const renderMainContent = () => {
    if (hasError) {
      return loadedLayout;
    }
    if (checkoutStore?.skipCheckoutFlowStatus === SkipCheckoutFlowStatus.FetchingData) {
      return <SkipCheckoutLayout status={SkipCheckoutFlowStatus.FetchingData} />;
    }
    if (checkoutStore?.skipCheckoutFlowStatus === SkipCheckoutFlowStatus.PlacingOrder) {
      return <SkipCheckoutLayout status={SkipCheckoutFlowStatus.PlacingOrder} />;
    }
    if (isLoading) {
      return loadingLayout;
    }
    return loadedLayout;
  };

  return (
    <div
      data-hook={CheckoutAppPageDataHook.root}
      style={pageTheme}
      className={isMobile ? 'mobile-view' : undefined}
      dir={experiments.enabled(SPECS.RTLCheckoutSkeleton) ? getDirValue(isRTL) : undefined}>
      <TPAComponentsProvider value={{rtl: isRTL}}>{renderMainContent()}</TPAComponentsProvider>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default CheckoutApp;
